/**
 * Placing dictated text into a field the designer may already have typed in.
 *
 * Getting this wrong is immediately visible — a missing space glues two
 * sentences together, a stray one leaves a gap before a comma — so the rules
 * live here with tests rather than inline in an event handler.
 */

export interface Insertion {
  value: string;
  /** Where the caret should land afterwards. */
  cursor: number;
}

/** Punctuation that should hug the preceding word rather than take a space. */
const HUGS_LEFT = /^[,.;:!?)\]}'"]/;
/** Characters after which a following space would look wrong. */
const NO_SPACE_AFTER = /[([{"'\s‘“]$/;

/**
 * Insert dictation into `existing`, replacing the selection when given one.
 *
 * Whitespace is normalized to exactly one space at the join, except where
 * punctuation or an opening bracket says otherwise. Appends at the end when no
 * selection is supplied.
 */
export function insertDictation(
  existing: string,
  addition: string,
  at?: { start: number; end: number },
): Insertion {
  const text = addition.trim();
  if (!text) return { value: existing, cursor: existing.length };

  const start = at ? clamp(at.start, 0, existing.length) : existing.length;
  const end = at ? clamp(Math.max(at.start, at.end), 0, existing.length) : existing.length;

  const before = existing.slice(0, start);
  const after = existing.slice(end);

  const needsLeadingSpace = before.length > 0
    && !NO_SPACE_AFTER.test(before)
    && !HUGS_LEFT.test(text);
  const lead = needsLeadingSpace ? ' ' : '';

  // Only add a trailing space when text follows and doesn't already start with
  // one or with punctuation that hugs.
  const needsTrailingSpace = after.length > 0
    && !/^\s/.test(after)
    && !HUGS_LEFT.test(after);
  const tail = needsTrailingSpace ? ' ' : '';

  const value = before + lead + text + tail + after;
  return { value, cursor: (before + lead + text).length };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
